import { FC, ReactNode, useCallback, useState } from "react";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { PrimaryButton } from "./PrimaryButton";
import { IconButton } from "./IconButton";
import { DropdownMenu } from "../DropdownMenu";
import { DropdownOption } from "./types";
import { BaseComponentProps } from "../types";
import { primaryButtonActive, primaryButtonHover } from "./commonStyles";
import { expandIconAnimation } from "../expandIconAnimation";

export enum ComboButtonTestIds {
  Container = "combo-button-container",
  MainButton = "combo-button-mainButton",
  ArrowButton = "combo-button-arrowButton",
}

const ComboMainButton = styled(PrimaryButton)`
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  margin-right: 1px;
`;

type ComboIconButtonProps = {
  expanded: boolean;
};

const ComboIconButton = styled(IconButton)<ComboIconButtonProps>`
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  ${expandIconAnimation}
`;

const styleButtonComponents = (styles: FlattenSimpleInterpolation) => {
  return css`
    ${ComboMainButton}:not(:disabled) {
      ${styles}
    }
    ${ComboIconButton}:not(:disabled) {
      ${styles}
    }
  `;
};

type StyledComboButtonProps = {
  expanded: boolean;
};

const StyledComboButton = styled.div<StyledComboButtonProps>`
  display: inline-flex;
  ${(props) =>
    props.expanded
      ? styleButtonComponents(primaryButtonActive)
      : css`
          :hover {
            ${styleButtonComponents(primaryButtonHover)}
          }
          :active {
            ${styleButtonComponents(primaryButtonActive)}
          }
        `}
`;

const ComboButtonContainer = styled.span`
  position: relative;
  display: inline-block;
`;

const OptionList = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0px;
`;

type ComboButtonProps = BaseComponentProps<
  "button",
  {
    children: ReactNode;
    options: DropdownOption[];
    testId?: string;
    onClick?: () => void;
  }
>;

type useComboButtonArgs = {
  disabled: boolean;
};

const useComboButton = ({ disabled }: useComboButtonArgs) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => {
    if (!disabled) {
      setOpen((prevOpen) => !prevOpen);
    }
  }, [disabled, setOpen]);
  const closeMenu = useCallback(() => {
    if (!disabled) {
      setOpen(false);
    }
  }, [disabled, setOpen]);

  return { open: !disabled && open, toggleOpen, closeMenu };
};

export const ComboButton: FC<ComboButtonProps> = ({
  children,
  options,
  disabled = false,
  testId,
  onClick,
  ...props
}) => {
  const { open, toggleOpen, closeMenu } = useComboButton({ disabled });

  const optionsWithCloseOnClick = options.map((option) => {
    const newOnClick: DropdownOption["onClick"] = (event) => {
      option.onClick(event);
      closeMenu();
    };
    return {
      ...option,
      onClick: newOnClick,
    };
  });

  return (
    <ComboButtonContainer>
      <StyledComboButton
        data-testid={testId ?? ComboButtonTestIds.Container}
        expanded={open}
      >
        <ComboMainButton
          data-testid={
            testId ? `${testId}-mainButton` : ComboButtonTestIds.MainButton
          }
          onClick={onClick || toggleOpen}
          disabled={disabled}
          {...props}
        >
          {children}
        </ComboMainButton>
        <ComboIconButton
          data-testid={
            testId ? `${testId}-arrowButton` : ComboButtonTestIds.ArrowButton
          }
          onClick={toggleOpen}
          disabled={disabled}
          {...props}
          expanded={open}
          iconName="chevron-down"
        />
      </StyledComboButton>
      {open && (
        <OptionList>
          <DropdownMenu>
            {optionsWithCloseOnClick.map((option, i) => (
              <li key={i} onClick={option.onClick}>
                {option.label}
              </li>
            ))}
          </DropdownMenu>
        </OptionList>
      )}
    </ComboButtonContainer>
  );
};
